import "./license-log.module.css";

import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { filterData, SearchType } from "filter-data";
import { cloneDeep } from "lodash";

// Const
import { timeFormat } from '../../../../const/nuclias/dateFormat';
import { defaultActionTypeList, columnList, defaultSearch, searchType } from "./const";

// Slice
import { uiActions } from "../../../../stores/slice/ui";

// Hook
import { useSortableTable } from '../../../../hooks/useSortableTable';
import useStickyHeader from '../../../../hooks/useStickyHeader';

// Component
import InlineTitle from "../../../../components/InlineTitle";
import { getPageLimit, sliceDataByPage, sortOrderType, renderTableHeader } from "../../../../components/PaginationContainer";
import Button from "../../../../components/Button";
import DropdownWithTimeframe, { getTimeFrameSetting, getTimeFrameKey } from "../../../../components/DropdownWithTimeframe";
import DropdownWithAdvancedSearch from "../../../../components/DropdownWithAdvancedSearch";
import Input from "../../../../components/Input";
import DropdownWithItem from "../../../../components/DropdownWithItem";
import TooltipDialog from "../../../../components/TooltipDialog";
import LicenseLogTable from "./components/LicenseLogTable";
import LicenseLogPagination from "./components/LicenseLogPagination";
import GenerateReport, { defaulGenerateReportMessages } from './modals/GenerateReport';

// Service
import AuthLicenseService from "../../../../services/Auth.License.service";

// Util
import { messageKeyPrefix } from "../../../../utils/message.util";
import { fromUrl, textToCsv } from "../../../../utils/downloadFile.util";
import { formatDateFromTimestamp, getTimeframe } from "../../../../utils/date-time.util";

const LicenseLog = ({ isOrgLevel, messages, setMessages }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const customTimeframe = getTimeframe(getTimeFrameSetting(), getTimeFrameKey().last24hrs);
  const pageContext = useSelector(state => state.pageContext);
  const [selectedTimeframe, setSelectedTimeframe] = useState(null);
  const { tableRef, stickyStyle } = useStickyHeader();

  // Table & paging
  const [selectedPage, setSelectedPage] = useState(1);
  const [selectedPageLimit, setSelectedPageLimit] = useState(getPageLimit().default);
  const [originLicenseLogList, setOriginLicenseLogList] = useState([]);
  const [filteredLicenseLogList, setFilteredLicenseLogList] = useState([]);
  const [currentoriginLicenseLogList, setCurrentoriginLicenseLogList] = useState([]);

  // Sorting
  const [tableData, handleSorting] = useSortableTable(filteredLicenseLogList, columnList);
  const [sortField, setSortField] = useState('');
  const [order, setOrder] = useState(sortOrderType.asc);

  // Searching
  const [search, setSearch] = useState(cloneDeep(defaultSearch));

  // Modal
  const [generateReportMessages, setGenerateReportMessages] = useState({ ...defaulGenerateReportMessages });
  const [isGenerateReportModal, setGenerateReportModal] = useState(false);

  // Variable
  let selectedOrg = null;
  const actionTypeList = cloneDeep(defaultActionTypeList);
  const description = () => {
    if (!selectedTimeframe?.postTimeStamp?.fromDate)
      return;

    return t(
      '8301d01641',
      {
        number: filteredLicenseLogList.length || 0,
        date: formatDateFromTimestamp(selectedTimeframe.postTimeStamp.fromDate * 1000, timeFormat.yDate)
      }
    );
  }

  if (isOrgLevel) {
    selectedOrg = pageContext.selectedOrg;
  } else {
    selectedOrg = pageContext.si;
  }

  // Method
  const init = () => {
    const updatedLicenseLogData = [];
    const parseRawData = (rawData) => {
      for (const index in rawData) {
        const item = rawData[index];
        const log = {
          account: item.username || '',
          time: formatDateFromTimestamp(new Date(item.timestamp), timeFormat.yDateAndTime),
          timestamp: item.timestamp,
          licenseKey: item.licenseKey,
          actionTypeLabel: null,
          action: item.action,
          details: item.description
        };

        for (const actionType of actionTypeList) {
          if (item.action.toLowerCase() === actionType.value.toLowerCase()) {
            log.actionTypeLabel = actionType.title;
          }
        }

        updatedLicenseLogData.push(log);
      }

      return updatedLicenseLogData;
    }

    let pageId = 1;
    let rawData = [];

    const fetchData = async () => {
      dispatch(uiActions.showSpinner());
      cleanSearch();

      const postData = {
        orgId: selectedOrg.id,
        fromDate: selectedTimeframe.postTimeStamp.fromDate * 1000,
        toDate: selectedTimeframe.postTimeStamp.toDate * 1000,
        dbPageId: pageId,
        dbPageSize: selectedPageLimit
      }

      try {
        const { data, error } = await AuthLicenseService.getLicenseLog(postData);

        if (error) {
          throw error
        }

        rawData = rawData.concat(data.logs);
        if (rawData.length < data.paging.total) {
          pageId++;
          fetchData();
        } else {
          const logList = parseRawData(rawData);

          setOriginLicenseLogList(logList);
          setFilteredLicenseLogList(logList);
          setSelectedPage(1);
          dispatch(uiActions.hideSpinner());
        }
      }
      catch (error) {
        setMessages({
          ...messages,
          error: error.code,
          success: null,
        });

        dispatch(uiActions.hideSpinner());
      }
    }

    fetchData()
  }

  const cleanSearch = () => {
    setSearch(cloneDeep(defaultSearch));
    setFilteredLicenseLogList(originLicenseLogList);
  }

  const handleSearch = (field, value) => {
    let udpatedSearch = cloneDeep(defaultSearch);

    switch (field) {
      case searchType.fuzzy.value:
        udpatedSearch.fuzzy = value;
        break;
      case searchType.account.value:
        udpatedSearch.fuzzy = null;
        udpatedSearch.account = value;
        udpatedSearch.actionType = search.actionType;
        break;
      case searchType.actionType.value:
        udpatedSearch.fuzzy = null;
        udpatedSearch.account = search.account;
        udpatedSearch.actionType = value;
        break;
      default:
        return;
    }

    setSearch(udpatedSearch);

    if (field === searchType.fuzzy.value) {
      const searchConditionList = [
        {
          key: ['account', 'licenseKey', 'actionTypeLabel', 'details'],
          value: udpatedSearch.fuzzy,
          type: SearchType.LK,
        }
      ];

      const fuzzySearchResult = filterData(originLicenseLogList, searchConditionList);
      setFilteredLicenseLogList(fuzzySearchResult);
      setSelectedPage(1);
    } else {
      let searchConditionList = [];
      searchConditionList.push({
        key: ['account'],
        value: udpatedSearch.account,
        type: SearchType.LK
      });
      searchConditionList.push({
        key: ['action'],
        value: udpatedSearch.actionType,
        type: SearchType.EQ
      });

      const fuzzySearchResult = filterData(originLicenseLogList, searchConditionList);
      setFilteredLicenseLogList(fuzzySearchResult);
      setSelectedPage(1);
    }
  }

  const getSearchKeyword = (field) => {
    if (field === searchType.fuzzy.value && search.fuzzy) {
      return search.fuzzy;
    }

    if (field === searchType.fuzzy.value && !search.fuzzy) {
      let keywordList = []; // combined search

      if (search.account) {
        const label = t(searchType.account.label);
        keywordList.push(`(${label}: ${search.account})`);
      }

      if (search.actionType) {
        const label = t(searchType.actionType.label);
        let title = null;

        for (const actionType of actionTypeList) {
          if (search.actionType.toLowerCase() === actionType.value.toLowerCase()) {
            title = t(actionType.title);
          }
        }

        keywordList.push(`(${label}: ${title})`);
      }

      return keywordList.join(` ${t('19bf32b872')} `);
    }

    if (field === searchType.account.value) {
      return search.account;
    }
  }

  const getActionTypeList = () => {
    if (!search.actionType) {
      return actionTypeList;
    }

    for (const actionType of actionTypeList) {
      actionType.isActive = false;
      if (search.actionType.toLowerCase() === actionType.value.toLowerCase()) {
        actionType.isActive = true;
      }
    }

    return actionTypeList;
  }

  getActionTypeList();

  const openGenerateReportModal = () => {
    setGenerateReportMessages({ ...defaulGenerateReportMessages });
    setGenerateReportModal(true);
  }

  const downloadReport = async () => {
    if (!isOrgLevel) {
      try {
        const { data, error } = await AuthLicenseService.getLicenseLogUrl({ mspId: selectedOrg.id });
        if (error) {
          throw error;
        }

        fromUrl(data.downloadUrl);
      } catch (error) {
        setMessages({
          ...messages,
          error: error.code,
          success: null,
        });
      }
    } else {
      const params = {
        orgId: selectedOrg.id,
        fromDate: selectedTimeframe.postTimeStamp.fromDate * 1000,
        toDate: selectedTimeframe.postTimeStamp.toDate * 1000
      };

      try {
        const data = await AuthLicenseService.getLicenseLogCsv(params);

        if (data.error) {
          throw data.error;
        }

        const fileName = `ORG_License Change log_${formatDateFromTimestamp(null, timeFormat.csvDownloadFormat)}.csv`;
        textToCsv(fileName, data);
      } catch (error) {
        setMessages({
          ...messages,
          error: error.code,
          success: null,
        });
      }
    }
  }

  const generateReport = useCallback(async () => {
    const params = {
      orgId: selectedOrg.id,
      fromDate: selectedTimeframe.postTimeStamp.fromDate * 1000,
      toDate: selectedTimeframe.postTimeStamp.toDate * 1000
    };

    try {
      const { error } = await AuthLicenseService.generateLicenseLog(params);

      if (error) {
        throw error;
      }

      setGenerateReportModal(false);
    } catch (error) {
      let errorMessages = t(`${messageKeyPrefix.e.s}${error.code}`);
      setGenerateReportMessages({ ...defaulGenerateReportMessages, show: true, label: errorMessages });
    }
  }, [selectedOrg.id, selectedTimeframe]);

  // Side effect
  useEffect(() => {
    selectedTimeframe && init();
  }, [selectedTimeframe]);

  useEffect(() => {
    setCurrentoriginLicenseLogList(sliceDataByPage(tableData, selectedPage, selectedPageLimit));
  }, [selectedPage, selectedPageLimit, tableData]);

  return (
    <div className='layout-container layout-container--column layout-container--fluid'>
      <InlineTitle isNonUnderline>
        <span className='form-title'>{t('a056c9a163')} : </span>
        <DropdownWithTimeframe
          alignEnd={true}
          customDefaultTimeframe={customTimeframe}
          onInit={initTimeFrame => setSelectedTimeframe(initTimeFrame)}
          onChange={setSelectedTimeframe}
        />

        <DropdownWithAdvancedSearch
          value={getSearchKeyword('fuzzy')}
          alignEnd={true}
          dataBsToggleOnButton={true}
          dropdownMenuStyle={{ minWidth: 371 }}
          buttonOnClick={cleanSearch}
          onChange={e => handleSearch(searchType.fuzzy.value, e.target.value.trim())}
          onClick={cleanSearch}
        >
          <li>
            <div className='form-title'>{t('3841f18e82')}</div>
            <div className='form-field'>
              <Input
                type='text'
                value={getSearchKeyword('account')}
                placeholder='abc@mail.com'
                onChange={e => handleSearch(searchType.account.value, e.target.value.trim())}
              />
            </div>
          </li>
          <li>
            <div className='form-title'>{t('004bf6c9a4')}</div>
            <DropdownWithItem
              id="action-dropdown"
              type="normal"
              selectedItem={actionTypeList.filter(actionType => actionType.isActive)[0]}
              itemList={getActionTypeList()}
              onClick={actionType => handleSearch(searchType.actionType.value, actionType.value)}
            />
          </li>
        </DropdownWithAdvancedSearch>

        {!isOrgLevel && (
          <Button
            label={t('de3e350386')}
            className='btn-grey'
            onClick={openGenerateReportModal}
          />
        )}

        <Button
          label={t('801ab24683')}
          className='btn-grey'
          onClick={downloadReport}
        />

        <TooltipDialog placement='left' title={t('9d53acb410')}></TooltipDialog>
      </InlineTitle>

      <LicenseLogTable
        tableRef={tableRef}
        stickyStyle={stickyStyle}
        tableHeader={() => renderTableHeader(columnList, order, setOrder, sortField, setSortField, handleSorting)}
        currentoriginLicenseLogList={currentoriginLicenseLogList}
        selectedPage={selectedPage}
        selectedPageLimit={selectedPageLimit}
      />

      <LicenseLogPagination
        description={() => description()}
        filteredLicenseLogList={filteredLicenseLogList}
        selectedPage={selectedPage}
        setSelectedPage={setSelectedPage}
        setSelectedPageLimit={setSelectedPageLimit}
      />

      {/* Generate report modal */}
      <GenerateReport
        isGenerateReportModal={isGenerateReportModal}
        setGenerateReportModal={setGenerateReportModal}
        generateReportMessages={generateReportMessages}
        setGenerateReportMessages={setGenerateReportMessages}
        generateReport={generateReport}
      />
    </div >
  )
}

export default LicenseLog;